/* ------------------------------------------------------------------
 * src/Api/apiClient.jsx
 *
 * Axios instance + session handling in one file.
 * No baseURL — every call passes a complete URL from ApiConstants.
 *
 *  - attaches the auth header automatically  (opt out: { skipAuth: true })
 *  - clears the session and redirects on 401 / 403
 *  - request() flattens ApiResponseModel into { success, data, message }
 *
 * Need the raw ApiResponseModel? Import the default export and call
 * apiClient.get(...) directly — the interceptors still apply.
 * ------------------------------------------------------------------ */

import axios from 'axios';

/* ==================================================================
 * SESSION — the only place in the app that touches sessionStorage
 * ================================================================== */

const SESSION_KEYS = {
  USER: 'user',
  TOKEN: 'token',
  STORE_CONFIG: 'storeConfig',
};

export const getSessionData = () => {
  try {
    const user = sessionStorage.getItem(SESSION_KEYS.USER);
    const storeConfig = sessionStorage.getItem(SESSION_KEYS.STORE_CONFIG);

    return {
      user: user ? JSON.parse(user) : null,
      token: sessionStorage.getItem(SESSION_KEYS.TOKEN) || null,
      storeConfig: storeConfig ? JSON.parse(storeConfig) : null,
    };
  } catch (e) {
    console.error('Failed to read session:', e);
    return { user: null, token: null, storeConfig: null };
  }
};

export const setSessionData = ({ user, token, storeConfig }) => {
  try {
    if (user) sessionStorage.setItem(SESSION_KEYS.USER, JSON.stringify(user));
    if (token) sessionStorage.setItem(SESSION_KEYS.TOKEN, token);
    if (storeConfig) {
      sessionStorage.setItem(SESSION_KEYS.STORE_CONFIG, JSON.stringify(storeConfig));
    }
  } catch (e) {
    console.error('Failed to write session:', e);
  }
};

export const clearSessionData = () => {
  Object.values(SESSION_KEYS).forEach((key) => sessionStorage.removeItem(key));
};

/* ==================================================================
 * CLIENT
 * ================================================================== */

const apiClient = axios.create({
  timeout: 20000,
});

apiClient.interceptors.request.use((config) => {
  if (config.skipAuth) return config;

  const { token } = getSessionData();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if ((status === 401 || status === 403) && !error.config?.skipAuth) {
      clearSessionData();
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

/* ==================================================================
 * HELPERS — flatten ApiResponseModel so callers skip the try/catch
 * ================================================================== */

/**
 * @param {object} config   axios config (url must be complete)
 * @param {*}      fallback what `data` should be on failure ([] for lists)
 * @returns {{ success: boolean, data: *, message: string, status: number }}
 */
export const request = async (config, fallback = null) => {
  try {
    const response = await apiClient(config);

    if (response.data?.status === 'success') {
      return {
        success: true,
        data: response.data.payload ?? fallback,
        message: response.data.message,
        status: response.status,
      };
    }

    return {
      success: false,
      data: fallback,
      message: response.data?.message || 'Request failed',
      status: response.status,
    };
  } catch (error) {
    if (error.response) {
      const body = error.response.data;
      console.error('API error:', config.url, body);
      return {
        success: false,
        data: fallback,
        message: body?.message || body?.payload?.message || 'Request failed',
        status: error.response.status,
      };
    }

    console.error('Network error:', config.url, error);
    return {
      success: false,
      data: fallback,
      message: 'Something went wrong. Please try again later.',
      status: 0,
    };
  }
};

export const get = (url, options = {}, fallback = null) =>
  request({ method: 'GET', url, ...options }, fallback);

export const post = (url, data, options = {}, fallback = null) =>
  request({ method: 'POST', url, data, ...options }, fallback);

export const put = (url, data, options = {}, fallback = null) =>
  request({ method: 'PUT', url, data, ...options }, fallback);

export const del = (url, options = {}, fallback = null) =>
  request({ method: 'DELETE', url, ...options }, fallback);

/* Spring endpoints expecting x-www-form-urlencoded */
export const postForm = (url, fields, options = {}, fallback = null) => {
  const body = new URLSearchParams();
  Object.entries(fields).forEach(([key, value]) => body.append(key, value));

  return request(
    {
      method: 'POST',
      url,
      data: body,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      ...options,
    },
    fallback
  );
};

export default apiClient;